'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { isSearchChord } from './search-chord';
import {
  AppWindow,
  Check,
  Compass,
  FileText,
  Grid2x2Plus,
  Image as ImageIcon,
  Music,
  Search,
  SlidersHorizontal,
  Video,
  X,
} from 'lucide-react';
import { BotIcon as Bot } from '@/components/icons/bot-icon';
import {
  useWorkspaceActions,
  useWorkspaceItems,
} from '@/components/workspace/store';
import {
  resolveItemLabel,
  useLiveContentMeta,
} from '@/components/workspace/item-label';
import { useWorkspaceCardActions } from '@/components/workspace/card-actions-context';
import { useAuth } from '@/components/auth-provider';
import { GradientPillButton } from '@/components/ui/gradient-pill-button';
import { useT, useTranslation } from '@/components/i18n-provider';
import { Avatar } from '@/components/avatar';
import { openWorkspacePanel } from '@/components/workspace/panels/panel-events';
import { cn } from '@/lib/utils';
import { useImportWidgetUrl } from '@/hooks/use-import-widget-url';
import { fetchPersonalAgent, type PersonalAgentLookup } from '@/hooks/use-personal-agent';
import { useFullscreenNav } from '@/components/workspace/fullscreen-nav';
import { PopoverPortal, useAnchorRect } from '@/components/ui/popover-portal';
import { createPortal } from 'react-dom';

// =============================================================================
// Type filter — the search popover now narrows by entity TYPE in addition to
// SCOPE. The two dimensions live in one chip; the chip label collapses
// gracefully so the default "All / All" state reads as just "All". A `widgets`
// type routes the fetcher at `content_type=widget`-aware endpoints so the
// search becomes the front door for the agent + widget marketplace.
// =============================================================================
type Type = 'all' | 'agents' | 'widgets' | 'posts' | 'content';

const TYPE_KEYS: Type[] = ['all', 'agents', 'widgets', 'posts', 'content'];

type AgentResult = {
  id: string;
  username: string;
  displayName?: string | null;
  avatarUrl?: string | null;
};
type PostResult = {
  id: string;
  title?: string | null;
  content?: string | null;
  thumbnailUrl?: string | null;
  mediaUrl?: string | null;
  mediaType?: string | null;
  author?: { username?: string | null; avatarUrl?: string | null; accountType?: string | null } | null;
  /** Present when the post has a paired widget content row (see LEFT JOIN
   *  in /api/posts and /api/discovery/posts). Drives widget-aware row
   *  rendering and the "Add widget" trailing button. */
  widgetContentId?: string | null;
  widgetMetadata?: Record<string, unknown> | null;
  installed?: boolean;
};

/**
 * Pick the best image to show next to a post in the quick search.
 * Prefers an explicit `thumbnailUrl`, then falls back to `mediaUrl`
 * for image-type media so posts that only carry the original asset
 * (no separate thumbnail row) still show a preview instead of the
 * generic "P" placeholder.
 */
function postPreviewUrl(p: PostResult): string | null {
  if (p.thumbnailUrl) return p.thumbnailUrl;
  if (p.mediaUrl && p.mediaType?.startsWith('image')) return p.mediaUrl;
  return null;
}

/**
 * Pick a Lucide icon for the search row's leading placeholder based on
 * the content family. Widgets get their own glyph (AppWindow) — without
 * this every widget row showed a generic "C" which read as random
 * noise. Other families fall through to a sensible icon, and finally
 * to a single-letter placeholder for anything we don't recognise.
 */
function contentTypeIcon(contentType?: string): React.ReactNode | null {
  switch (contentType) {
    case 'widget':
      return <AppWindow className="h-4 w-4 text-primary" />;
    case 'image':
      return <ImageIcon className="h-4 w-4 text-muted-foreground" />;
    case 'video':
      return <Video className="h-4 w-4 text-muted-foreground" />;
    case 'audio':
      return <Music className="h-4 w-4 text-muted-foreground" />;
    case 'document':
      return <FileText className="h-4 w-4 text-muted-foreground" />;
    default:
      return null;
  }
}

function renderWorkspaceLeading(w: WorkspaceResult): React.ReactNode {
  if (w.type === 'agent') {
    return (
      <Avatar
        seed={w.avatarSeed || w.itemId}
        type="agent"
        src={w.avatarUrl ?? undefined}
        className="h-8 w-8"
      />
    );
  }
  if (w.thumbnailUrl) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={w.thumbnailUrl}
        alt=""
        className="h-8 w-8 rounded-md object-cover"
      />
    );
  }
  const icon = contentTypeIcon(w.contentType);
  if (icon) {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted">
        {icon}
      </div>
    );
  }
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-[10px] font-mono uppercase text-muted-foreground">
      {w.type.slice(0, 1)}
    </div>
  );
}
type ContentResult = {
  id: string;
  displayName: string;
  contentType: string;
  mimeType: string;
  previewUrl?: string | null;
};
type WorkspaceResult = {
  itemId: string;
  refId: string | null;
  type: 'agent' | 'content' | 'post';
  title: string;
  subtitle?: string;
  avatarUrl?: string | null;
  avatarSeed?: string;
  thumbnailUrl?: string | null;
  contentType?: string;
};

type Group = {
  agents: AgentResult[];
  posts: PostResult[];
  contents: ContentResult[];
  workspace: WorkspaceResult[];
};

const EMPTY: Group = { agents: [], posts: [], contents: [], workspace: [] };

type Scope = 'all' | 'workspace' | 'account' | 'web';

const SCOPE_LABELS: Record<Scope, string> = {
  all: 'All',
  workspace: 'Workspace',
  account: 'Account',
  web: 'Web of agents',
};

// Module-scope label table: no hook can reach here. Values stay English
// until the table is refactored to resolve through `t` at the call site.
const TYPE_LABELS: Record<Type, string> = {
  all: 'All',
  agents: 'Agents',
  widgets: 'Apps',
  posts: 'Posts',
  content: 'Content',
};

/**
 * Combined chip label. The empty "All / All" state collapses to just
 * "All" so the default never looks like two redundant filters; setting
 * only one dimension shows that dimension's label; setting both joins
 * them with a thin separator so the chip stays compact.
 */
function filterChipLabel(scope: Scope, type: Type): string {
  const scopeSet = scope !== 'all';
  const typeSet = type !== 'all';
  if (!scopeSet && !typeSet) return 'All';
  if (scopeSet && !typeSet) return SCOPE_LABELS[scope];
  if (!scopeSet && typeSet) return TYPE_LABELS[type];
  return `${SCOPE_LABELS[scope]} · ${TYPE_LABELS[type]}`;
}

const AGENT_PREFILL_FALLBACK = 'Create a new agent for ';
const WIDGET_PREFILL_FALLBACK = 'Create a new app for ';

/**
 * Bring an item that is already on the canvas into view, through the canvas's
 * `ws:focus-item` chokepoint: a window is framed at 100% the way the taskbar
 * frames it, anything else is centred at the current zoom. Search used to call
 * `setCenter` itself with a 0.8 zoom floor, which pulled a Stage-view camera
 * to 80% outside the Stage; workspace zoom is disabled.
 */
function focusOnCanvas(itemId: string): void {
  window.dispatchEvent(new CustomEvent('ws:focus-item', { detail: { itemId } }));
}

/**
 * Top-center "search everything".
 *
 * Debounced cross-search across the current workspace, the user's
 * account (their own agents/posts/content), and the public web of
 * agents (discovery). The filter chip combines TYPE (agents / widgets /
 * posts / content / all) and SCOPE (workspace / account / web / all).
 *
 * Empty-query state shows trending content for the active type, with
 * Create CTA rows for the agent + widget marketplace at the top:
 *   - type=agents  → "Create agent" + Trending agents
 *   - type=widgets → "Create widget" + Trending widgets
 *   - type=all     → Create agent + Create widget (split row) + trending
 * The gradient "Open discovery" CTA is always visible in the filter bar.
 *
 * Click a result to either focus the existing widget on the canvas (if
 * there's already one with the same ref) or drop a new widget at the
 * viewport centre. Per-row trailing Grid+ button on widget posts and
 * agent rows triggers `addItem` directly via the workspace card actions
 * (dispatching native widget types correctly via `addWidget`).
 */
export function SearchEverything({
  headless = false,
  standalone = false,
}: {
  /**
   * Rail (V2) variant. When set, the in-flow search bar is NOT rendered —
   * the component mounts purely as the command-palette host + the
   * `workspace:open-search` / Cmd+K listener, and the popover opens as a
   * centered overlay with the search input docked inside it. The rail's
   * Search button summons it. Default `false` keeps the legacy top-ribbon
   * bar behaviour byte-identical.
   */
  headless?: boolean;
  /**
   * Empty-workspace inline variant: render the normal bar + results
   * popover anchored to ITSELF (results open in place under the same
   * input), but DON'T register the global `workspace:open-search` /
   * Cmd+K listeners — the rail's headless palette owns those. This lets
   * the empty-state hero embed a real, in-place search without two
   * instances both answering the same global triggers.
   */
  standalone?: boolean;
} = {}) {
  const { t } = useTranslation();
  // Missing translation keys round-trip as the key path itself. Wrap
  // every call so each string has a guaranteed English fallback while
  // we ship the keys across all locale files.
  const tx = (key: string, fallback: string, params?: Record<string, string | number>): string => {
    const v = t(key, params);
    return v && v !== key ? v : fallback;
  };
  const { user } = useAuth();
  const { addItem, findFirstByRef } = useWorkspaceActions();
  const wsItems = useWorkspaceItems();
  // "On this workspace" rows are titled live-first through item-label.ts, so
  // searching for a renamed app finds it under the name it has now, and the
  // row reads the same as its rail puck (the 2026-09-13 browser pass).
  const liveContentMeta = useLiveContentMeta();
  const wsCardActions = useWorkspaceCardActions();
  const { openHref } = useFullscreenNav();
  // Inline error for the "Create agent / Create app" rows: shown inside the
  // popover (which stays open) when the caller's personal agent cannot be
  // resolved or the canvas add fails. Cleared on every attempt and on close.
  const [createNotice, setCreateNotice] = useState<string | null>(null);

  // Narrow-viewport flag for placeholder copy: the longer anon
  // placeholder truncates to "Search agents and" on a phone, so we fall
  // back to the short one there. Reactive (resize) + SSR-safe (defaults
  // to desktop, flips on mount).
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const [q, setQ] = useState('');
  const [scope, setScope] = useState<Scope>('all');
  const [type, setType] = useState<Type>('all');
  const [results, setResults] = useState<Group>(EMPTY);
  // Plan v3-05 (search-everything-url-fallback) — URL import state.
  // Inline-only fallback for Tier C (NO modal, NO delegation): the
  // banner sits in the result list itself so the user keeps the rest
  // of the search affordances available. `urlImportNotice` carries
  // either the success toast text or a Tier C / SSRF rejection
  // message; it's cleared every time the input changes.
  const [urlImportNotice, setUrlImportNotice] = useState<{
    tone: 'ok' | 'denied' | 'error';
    text: string;
  } | null>(null);
  const { importUrl, loading: importingUrl } = useImportWidgetUrl();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // True when the popover was summoned by an external action (FAB
  // Agent/Widget, Cmd+K) rather than the user clicking the input
  // directly. Drives the dimmed backdrop that focuses attention on the
  // search funnel without obscuring the canvas state. Reset to false
  // every time the popover closes.
  const [summoned, setSummoned] = useState(false);
  // Closing helper — single point that resets transient state so the
  // next open starts clean (Type/Scope at "All", no stale typed query,
  // backdrop dismissed). External summoners (FAB → Widget) re-apply
  // their type filter via the same event listener that opened it, so
  // resetting here doesn't fight FAB-driven presets.
  const closePopover = () => {
    setOpen(false);
    setFiltersOpen(false);
    setSummoned(false);
    setQ('');
    setType('all');
    setScope('all');
    setCreateNotice(null);
  };

  // Cache trending fetches per (scope, type) for the lifetime of the
  // popover so re-opening the search bar is instant. Invalidated only
  // by the popover unmount.
  const trendingCacheRef = useRef<Record<string, Group>>({});
  const [trending, setTrending] = useState<Group>(EMPTY);
  const [trendingLoading, setTrendingLoading] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const portalBodyRef = useRef<HTMLDivElement>(null);
  const anchorRect = useAnchorRect(open, containerRef);

  // ---- summon via window event -----------------------------------------
  // Sibling chrome (the canvas right-click menu, hotkeys, the "+" FAB's
  // Agent/Widget items) can summon the search popover by dispatching
  // `workspace:open-search`. The optional `detail.type` payload presets
  // the type filter so FAB → Widget lands on the widget marketplace.
  // External summons set `summoned: true` so we render the dimmed
  // backdrop that pulls focus to the funnel. A direct click on the
  // input is treated as a normal toggle (no backdrop, no preset).
  useEffect(() => {
    if (standalone) return;
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ type?: Type; query?: string } | undefined>).detail;
      // When the summoner supplied a `query` (e.g. via the `?search=`
      // URL action) seed the input with it instead of resetting to
      // ''. A direct FAB tap has no `query` and still gets a clean
      // slate so a previous session's text doesn't leak in.
      if (typeof detail?.query === 'string') setQ(detail.query);
      else setQ('');
      setType(detail?.type && TYPE_KEYS.includes(detail.type) ? detail.type : 'all');
      setScope('all');
      setSummoned(true);
      setOpen(true);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        if (typeof detail?.query === 'string') {
          const el = inputRef.current;
          if (el) {
            const len = el.value.length;
            el.setSelectionRange(len, len);
          }
        }
      });
    };
    window.addEventListener('workspace:open-search', onOpen as EventListener);
    return () => window.removeEventListener('workspace:open-search', onOpen as EventListener);
  }, [standalone]);

  // ---- close popover on outside click ---------------------------------
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: PointerEvent) => {
      const target = e.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insidePortal = portalBodyRef.current?.contains(target);
      if (!insideTrigger && !insidePortal) {
        closePopover();
      }
    };
    document.addEventListener('pointerdown', onDoc, true);
    return () => document.removeEventListener('pointerdown', onDoc, true);
    // closePopover is stable for our use here (only setters inside);
    // omitting it keeps the listener from re-binding on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---- escape closes the popover --------------------------------------
  // Cmd/Ctrl+K is the global shortcut to summon the popover from
  // anywhere on the page. Escape closes it (filters menu first, then
  // the popover itself). We register both on `document` so the
  // shortcut works even when the user is focused on a canvas widget
  // or another input — except when typing into a separate `<input>`
  // or `<textarea>` (the editor / chat composer), to avoid clobbering
  // local Cmd+K bindings.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (filtersOpen) setFiltersOpen(false);
        else if (open) closePopover();
        return;
      }
      if (isSearchChord(e)) {
        // The rail's headless palette owns the global Cmd+K; a standalone
        // (empty-state) instance only opens via direct focus.
        if (standalone) return;
        const tgt = e.target as HTMLElement | null;
        const tag = tgt?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tgt?.isContentEditable) {
          // Allow ours to fire — the input is the search-everything
          // input itself (we own it).
          if (tgt !== inputRef.current) return;
        }
        e.preventDefault();
        // Rail V2: ⌘K focuses the in-panel Search view (the panel IS the
        // search surface now), not this centered overlay.
        if (headless) {
          openWorkspacePanel('left', { tab: 'search' });
          return;
        }
        // Re-summon semantics: open with fresh state.
        setQ('');
        setType('all');
        setScope('all');
        setSummoned(true);
        setOpen(true);
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filtersOpen, standalone, headless]);

  // ---- workspace-scope search (client-side) ----------------------------
  // Runs the active query against widgets already on the canvas. Cheap,
  // synchronous, and includes docked items so the user can rehydrate
  // them via Focus. Type filter narrows by item kind so e.g. selecting
  // "widgets" only surfaces widget content nodes from the current canvas.
  const workspaceMatches = useMemo<WorkspaceResult[]>(() => {
    const term = q.trim().toLowerCase();
    if (scope !== 'all' && scope !== 'workspace') return [];
    const ranked: WorkspaceResult[] = [];
    for (const it of wsItems) {
      if (it.type !== 'agent' && it.type !== 'content' && it.type !== 'post') {
        continue;
      }
      const cache = (it.state.cache ?? {}) as Record<string, unknown>;
      const itemContentType = (cache.contentType as string) ?? undefined;
      // Narrow workspace hits by the active type filter so flipping to
      // "Widgets" only shows widget canvas items, etc.
      if (type === 'agents' && it.type !== 'agent') continue;
      if (type === 'posts' && it.type !== 'post') continue;
      if (type === 'content' && it.type !== 'content') continue;
      if (type === 'widgets') {
        if (it.type !== 'content' || itemContentType !== 'widget') continue;
      }
      const title = resolveItemLabel(it, liveContentMeta, it.type);
      const subtitle =
        it.type === 'agent' && cache.username
          ? `@${cache.username as string}`
          : (cache.mimeType as string) || undefined;
      const avatarUrl =
        it.type === 'agent' ? ((cache.avatarUrl as string) ?? null) : null;
      const avatarSeed =
        it.type === 'agent'
          ? ((cache.username as string) || (cache.displayName as string) || it.id)
          : undefined;
      const thumbnailUrl =
        it.type === 'post'
          ? ((cache.thumbnailUrl as string) ||
              ((cache.mediaType as string)?.startsWith('image')
                ? ((cache.mediaUrl as string) ?? null)
                : null))
          : it.type === 'content'
            ? ((cache.previewUrl as string) ?? null)
            : null;
      if (!term || `${title} ${subtitle ?? ''}`.toLowerCase().includes(term)) {
        ranked.push({
          itemId: it.id,
          refId: it.refId ?? null,
          type: it.type,
          title,
          subtitle,
          avatarUrl,
          avatarSeed,
          thumbnailUrl,
          contentType: itemContentType,
        });
      }
    }
    return ranked.slice(0, 8);
  }, [wsItems, liveContentMeta, q, scope, type]);

  // ---- URL detection (Plan v3-05 W3 search-everything-url-fallback) ---
  // Cheap heuristic — only triggers when the user pastes / types a
  // fully-qualified http(s) URL. Anything ambiguous falls through to
  // the normal text search so we never offer "Add as widget" on a
  // partial / accidental match.
  const detectedUrl = useMemo<string | null>(() => {
    const trimmed = q.trim();
    if (trimmed.length < 8) return null;
    try {
      const u = new URL(trimmed);
      if (u.protocol === 'http:' || u.protocol === 'https:') return u.toString();
    } catch {
      /* not a URL */
    }
    return null;
  }, [q]);

  // Reset the inline notice as soon as the user keeps typing or clears
  // the input — sticky banners that survive a query change feel broken.
  useEffect(() => {
    setUrlImportNotice(null);
  }, [q]);

  // ---- debounced fetched search ---------------------------------------
  // The set of endpoints we hit depends on the active scope AND the
  // type filter:
  //   - type=agents:  only agent endpoints
  //   - type=widgets: only post endpoints with content_type=widget
  //                   (account ⇒ /api/posts?scope_mine; web ⇒ discovery)
  //   - type=posts:   only post endpoints (no content_type)
  //   - type=content: only the content endpoint (account scope)
  //   - type=all:     same union as before, deduped by id
  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults(EMPTY);
      setLoading(false);
      return;
    }
    if (scope === 'workspace') {
      setResults(EMPTY);
      setLoading(false);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    const t = setTimeout(async () => {
      const enc = encodeURIComponent(term);
      const wantAccount = scope === 'all' || scope === 'account';
      const wantWeb = scope === 'all' || scope === 'web';
      const wantAgents = type === 'all' || type === 'agents';
      // Widgets are a DISTINCT fetch axis (content_type=widget), not a flavour
      // of the plain-posts query — `/api/users/me/posts` doesn't accept
      // `content_type`, and `/api/discovery/posts` excludes widgets by default.
      // So we fetch them separately and run that axis in BOTH 'all' and
      // 'widgets' modes; otherwise "Everything" never surfaces widgets.
      const wantWidgets = type === 'all' || type === 'widgets';
      const wantPlainPosts = type === 'all' || type === 'posts';
      const wantContent = type === 'all' || type === 'content';

      const json = (p: Promise<Response>) =>
        p.then((r) => (r.ok ? r.json() : null)).catch(() => null);

      const accountAgentsP = wantAccount && wantAgents
        ? json(fetch(`/api/users/me/agents?search=${enc}&limit=8`, { signal: ctrl.signal }))
        : Promise.resolve(null);
      const accountPostsP = wantAccount && wantPlainPosts
        ? json(fetch(`/api/users/me/posts?q=${enc}&limit=8`, { signal: ctrl.signal }))
        : Promise.resolve(null);
      // Account-scope widgets: /api/posts supports content_type (scope_mine
      // narrows to the caller's own; without a user we fall back to public).
      const accountWidgetsP = wantAccount && wantWidgets
        ? json(fetch(
            user?.id
              ? `/api/posts?scope_mine=${user.id}&content_type=widget&search=${enc}&limit=8`
              : `/api/posts?content_type=widget&search=${enc}&limit=8`,
            { signal: ctrl.signal },
          ))
        : Promise.resolve(null);
      // `owner=any` = the user's own content PLUS anything shared with them, so
      // a file a collaborator granted you is findable by name here.
      const accountContentP = wantAccount && wantContent
        ? json(fetch(`/api/content?owner=any&q=${enc}&limit=8`, { signal: ctrl.signal }))
        : Promise.resolve(null);
      // Owned PRIVATE widgets (e.g. a fresh Remix) live as `content`
      // rows, not posts — the widget axis above only queries posts. Under
      // the dedicated Widgets filter, also pull the caller's own widget
      // content rows so a just-remixed private widget is findable +
      // re-addable. (Under "All", `accountContentP` already returns them.)
      const accountOwnedWidgetsP = type === 'widgets' && wantAccount && !!user?.id
        ? json(fetch(`/api/content?owner=any&type=widget&q=${enc}&limit=8`, { signal: ctrl.signal }))
        : Promise.resolve(null);
      const webAgentsP = wantWeb && wantAgents
        ? json(fetch(`/api/discovery/agents?search=${enc}&limit=8`, { signal: ctrl.signal }))
        : Promise.resolve(null);
      const webPostsP = wantWeb && wantPlainPosts
        ? json(fetch(`/api/discovery/posts?q=${enc}&limit=8`, { signal: ctrl.signal }))
        : Promise.resolve(null);
      // Web-scope widgets (the public/marketplace widget catalog).
      const webWidgetsP = wantWeb && wantWidgets
        ? json(fetch(`/api/discovery/posts?q=${enc}&limit=8&content_type=widget`, { signal: ctrl.signal }))
        : Promise.resolve(null);
      const dedupe = <T extends { id: string }>(items: T[]): T[] => {
        const seen = new Set<string>();
        const out: T[] = [];
        for (const x of items) {
          if (seen.has(x.id)) continue;
          seen.add(x.id);
          out.push(x);
        }
        return out;
      };
      // Pull an array out of a `{ <key>: [...] } | { items: [...] } | [...]`
      // shaped axis response.
      const pick = <T,>(v: unknown, key: string): T[] => {
        const o = v as Record<string, unknown> | null | undefined;
        if (!o) return [];
        const byKey = (o as Record<string, unknown>)[key];
        if (Array.isArray(byKey)) return byKey as T[];
        const items = (o as { items?: unknown }).items;
        if (Array.isArray(items)) return items as T[];
        return Array.isArray(o) ? (o as unknown as T[]) : [];
      };
      // INCREMENTAL render: each axis paints into the results panel the moment
      // it resolves, rather than the whole panel waiting on the slowest of the
      // ~7 parallel fetches. Agent search returns in ~15ms server-side, so it
      // no longer sits behind a slow posts/content/web axis (the old single
      // `await Promise.all(...)` made every result hostage to the slowest one).
      const raw: Record<string, unknown> = {};
      const recompute = () => {
        if (ctrl.signal.aborted) return;
        const agents = dedupe<AgentResult>([
          ...pick<AgentResult>(raw.accAgents, 'agents'),
          ...pick<AgentResult>(raw.webAgents, 'agents'),
        ]);
        const posts = dedupe<PostResult>([
          ...pick<PostResult>(raw.accPosts, 'posts'),
          ...pick<PostResult>(raw.accWidgets, 'posts'),
          ...pick<PostResult>(raw.webPosts, 'posts'),
          ...pick<PostResult>(raw.webWidgets, 'posts'),
        ]);
        const contents = dedupe<ContentResult>([
          ...pick<ContentResult>(raw.accContent, 'items'),
          ...pick<ContentResult>(raw.accOwnedWidgets, 'items'),
        ]);
        setResults({ agents, posts, contents, workspace: [] });
      };
      const track = (p: Promise<unknown>, key: string): Promise<void> =>
        p.then((v) => {
          raw[key] = v;
          recompute();
        });
      void Promise.allSettled([
        track(accountAgentsP, 'accAgents'),
        track(accountPostsP, 'accPosts'),
        track(accountWidgetsP, 'accWidgets'),
        track(accountContentP, 'accContent'),
        track(accountOwnedWidgetsP, 'accOwnedWidgets'),
        track(webAgentsP, 'webAgents'),
        track(webPostsP, 'webPosts'),
        track(webWidgetsP, 'webWidgets'),
      ]).then(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    }, 220);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q, scope, type, user?.id]);

  // ---- trending (empty query) -----------------------------------------
  // Show trending agents / widgets / posts when the search bar is open
  // and the input is empty. Scoped to the active type filter so flipping
  // to "Widgets" hides the agents carousel etc. Results cached per
  // (scope, type) so re-opening the popover doesn't re-fetch.
  useEffect(() => {
    if (!open) return;
    if (q.trim()) return;
    if (scope === 'workspace') {
      setTrending(EMPTY);
      return;
    }
    const cacheKey = `${scope}::${type}`;
    const cached = trendingCacheRef.current[cacheKey];
    if (cached) {
      setTrending(cached);
      return;
    }
    const ctrl = new AbortController();
    setTrendingLoading(true);
    const run = async () => {
      const json = (url: string) =>
        fetch(url, { signal: ctrl.signal })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);
      const tasks: Array<Promise<unknown>> = [];
      const slot: { agents?: AgentResult[]; widgets?: PostResult[]; posts?: PostResult[] } = {};
      if (type === 'all' || type === 'agents') {
        tasks.push(
          json(`/api/featured/trending?sections=agents&limit=8`).then((d: { agents?: { items?: AgentResult[] } } | null) => {
            slot.agents = d?.agents?.items ?? [];
          }),
        );
      }
      if (type === 'all' || type === 'widgets') {
        tasks.push(
          json(`/api/posts?content_type=widget&sort=hot&limit=8`).then((d: { posts?: PostResult[] } | null) => {
            slot.widgets = d?.posts ?? [];
          }),
        );
      }
      if (type === 'all' || type === 'posts') {
        tasks.push(
          json(`/api/featured/trending?sections=posts&limit=8`).then((d: { posts?: { items?: PostResult[] } } | null) => {
            slot.posts = d?.posts?.items ?? [];
          }),
        );
      }
      await Promise.all(tasks);
      if (ctrl.signal.aborted) return;
      // Re-use the `posts` slot for widget posts when type=widgets so
      // the carousel renders through the same post-result path.
      const trendingPosts: PostResult[] = type === 'widgets'
        ? (slot.widgets ?? [])
        : type === 'agents'
          ? []
          : [...(slot.widgets ?? []), ...(slot.posts ?? [])];
      const next: Group = {
        agents: slot.agents ?? [],
        posts: trendingPosts,
        contents: [],
        workspace: [],
      };
      trendingCacheRef.current[cacheKey] = next;
      setTrending(next);
      setTrendingLoading(false);
    };
    void run();
    return () => ctrl.abort();
  }, [open, q, scope, type]);

  const totalCount =
    results.agents.length +
    results.posts.length +
    results.contents.length +
    workspaceMatches.length;
  const hasResults = totalCount > 0;
  const hasTrending =
    trending.agents.length + trending.posts.length > 0;
  const showTrending = open && !q.trim() && scope !== 'workspace';

  // ---- selection ------------------------------------------------------
  const focusOrAdd = async (
    selectType: 'agent' | 'post' | 'content',
    refId: string,
    cache?: Record<string, unknown>,
  ) => {
    closePopover();
    const existing = findFirstByRef(refId);
    if (existing) {
      focusOnCanvas(existing.id);
      return;
    }
    // Defer placement to the store — `addItem` runs the viewport-aware
    // `findEmptySpace` (anchored at the canvas's current centre,
    // padded against the live viewport rect, and biased to avoid
    // overlap with existing widgets) and then auto-focuses the camera
    // on whatever empty slot it picked. Passing explicit x/y here
    // would defeat both behaviours.
    await addItem({
      type: selectType,
      refId,
      state: { cache },
    }).catch(() => {});
  };

  const focusWorkspaceItem = (itemId: string) => {
    closePopover();
    if (!wsItems.some((x) => x.id === itemId)) return;
    focusOnCanvas(itemId);
  };

  const openDiscovery = () => {
    closePopover();
    // The bottom Discover panel is gone; browse lives in the Apps view (Explore).
    openWorkspacePanel('left', { tab: 'apps' });
  };

  /**
   * Drop a NEW instance of the caller's OWN personal agent onto the
   * canvas with a prefilled chat prompt — the entire "create agent /
   * create widget" flow stays inside the workspace. Each press
   * always spawns a fresh instance instead of focusing an existing
   * one: the previous behaviour (focus existing + navigate to the
   * agent's profile if it had a live chat) interrupted the user's
   * spatial context and the navigation fallback ended up being the
   * default any time a chat was already running.
   *
   * Until 2026-09-05 this spawned @robutler.factory. That agent is
   * retired: the personal agent carries `build_apps` / `build_agents`
   * itself. Its handle is resolved through `GET /api/agents/personal`
   * (never reconstructed from the username, which dangles after a
   * rename and names nothing for a guest) and the resolution happens
   * BEFORE `addItem`, so a failed lookup writes nothing. Failures are
   * shown inline in the (still open) popover rather than logged.
   *
   * Flow:
   *   1. `fetchPersonalAgent()` — a guest has no personal agent and is
   *      sent to the signup dialog, which upgrades the account in place.
   *   2. `addItem({ type: 'agent', refId, state: { cache: {
   *      prefillText } } })` — the store's `addItem` runs
   *      `findEmptySpace` so the widget lands in a visible empty
   *      spot without overlapping existing nodes.
   *   3. `AgentNode` → `AgentChatPane` → `ChatView` consumes
   *      `cache.prefillText`, seeds the composer textarea, focuses
   *      it, and parks the caret at the end of the seed prompt.
   */
  const routeToPersonalAgent = async (kind: 'agent' | 'widget') => {
    const prefill = kind === 'agent'
      ? tx('create.factoryPrefill', AGENT_PREFILL_FALLBACK)
      : tx('create.factoryWidgetPrefill', WIDGET_PREFILL_FALLBACK);
    setCreateNotice(null);
    let lookup: PersonalAgentLookup;
    try {
      lookup = await fetchPersonalAgent();
    } catch {
      setCreateNotice(tx('personalAgent.openFailed', 'Could not open your Robutler agent. Try again.'));
      return;
    }
    if (!lookup.agent) {
      if (lookup.guest) {
        closePopover();
        openHref('/signup');
        return;
      }
      setCreateNotice(tx('personalAgent.notReady', 'Your Robutler agent is not ready yet. Try again in a moment.'));
      return;
    }
    const agent = lookup.agent;
    try {
      await addItem({
        type: 'agent',
        refId: agent.id,
        state: {
          // The authoring chat needs more vertical room than the
          // default agent puck because users will be drafting agent /
          // widget specs in the composer — the bigger surface keeps
          // the prefilled prompt and the agent's welcome message
          // visible together without scrolling. `addItem` reads
          // `state.width/height` *before* the empty-space search, so
          // collisions are computed against the larger footprint.
          width: 450,
          height: 500,
          cache: {
            username: agent.username,
            displayName: agent.displayName ?? agent.username,
            avatarUrl: agent.avatarUrl ?? null,
            prefillText: prefill,
          },
        },
      });
      closePopover();
    } catch {
      setCreateNotice(tx('personalAgent.openFailed', 'Could not open your Robutler agent. Try again.'));
    }
  };

  const handleAddAgent = (a: AgentResult) => {
    // focusOrAdd already calls closePopover().
    void focusOrAdd('agent', a.id, {
      username: a.username,
      displayName: a.displayName,
      avatarUrl: a.avatarUrl,
    });
  };

  // Plan v3-05 W3 — handle "Add as widget" from a pasted URL.
  // W3 invariant: this path MUST NOT import `useDelegate`, MUST NOT
  // POST `/api/me/delegate`, and MUST NOT spawn any modal asking for
  // delegation consent. Tier C surfaces an INLINE banner only.
  const handleImportUrl = async () => {
    if (!detectedUrl || importingUrl) return;
    const result = await importUrl(detectedUrl);
    if (result.ok) {
      const tierText =
        result.tier === 'cooperative'
          ? tx('widget.tierA.fullCapabilities', 'App added: full capabilities')
          : tx('widget.tierB.viewOnlyBadge', 'App added: view-only badge');
      setUrlImportNotice({ tone: 'ok', text: tierText });
      if (wsCardActions && result.widgetContentId) {
        wsCardActions.addWidget({
          contentId: result.widgetContentId,
          title: detectedUrl,
          thumbnailUrl: null,
          metadata: result.readOnly ? { readOnly: true } : null,
          sourceWidgetPostId: result.postId,
        });
        closePopover();
      } else {
        // No card-actions context (e.g. summoned from outside the
        // canvas chrome) — fall back to addItem with the content id.
        if (result.widgetContentId) {
          void focusOrAdd('content', result.widgetContentId, {
            displayName: detectedUrl,
            contentType: 'widget',
            readOnly: result.readOnly ?? false,
          });
        }
      }
    } else if ('tier' in result && result.tier === 'denied') {
      // Tier C — inline message ONLY. No modal, no delegation prompt.
      setUrlImportNotice({
        tone: 'denied',
        text: tx(
          'widget.tierC.cantEmbed',
          "This site can't be embedded as a widget.",
        ),
      });
    } else if (!result.ok && 'code' in result && result.code === 'url_unsafe') {
      setUrlImportNotice({
        tone: 'error',
        text: tx('widget.url.unsafe', 'This URL is not safe to import.'),
      });
    } else {
      setUrlImportNotice({
        tone: 'error',
        text: (result as { message?: string }).message ?? 'Import failed',
      });
    }
  };

  const handleAddWidgetPost = (p: PostResult) => {
    if (!p.widgetContentId) {
      // Fallback for posts without a widget link — just open the post.
      void focusOrAdd('post', p.id, {
        title: p.title,
        mediaType: p.mediaType,
        thumbnailUrl: p.thumbnailUrl,
        mediaUrl: p.mediaUrl,
      });
      return;
    }
    if (!wsCardActions) {
      void focusOrAdd('content', p.widgetContentId, {
        displayName: p.title,
        contentType: 'widget',
      });
      return;
    }
    closePopover();
    wsCardActions.addWidget({
      contentId: p.widgetContentId,
      title: p.title ?? null,
      thumbnailUrl: p.thumbnailUrl ?? null,
      metadata: p.widgetMetadata ?? null,
      sourceWidgetPostId: p.id,
    });
  };

  // ---- shared bits ----------------------------------------------------
  // Combined Type+Scope filter chip. Two sections in one popover —
  // collapses to "All" when both axes are at default so the chip stays
  // compact and doesn't read as "All / All".
  const hasFilter = scope !== 'all' || type !== 'all';
  const filterBar = (
    <div className="flex shrink-0 items-center justify-between gap-2 px-2 pt-1.5 pb-1.5">
      <div className="relative">
        <div
          className={cn(
            'inline-flex items-center rounded-full border border-border/40 bg-background/40 text-[11px] font-medium',
            'text-muted-foreground transition-colors',
            !hasFilter && 'hover:bg-accent',
          )}
        >
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 hover:text-foreground transition-colors',
              hasFilter ? 'rounded-l-full' : 'rounded-full hover:bg-accent',
            )}
          >
            <SlidersHorizontal className="h-3 w-3" />
            <span className="text-foreground">{filterChipLabel(scope, type)}</span>
          </button>
          {hasFilter && (
            <button
              type="button"
              onClick={() => {
                setScope('all');
                setType('all');
              }}
              className={cn(
                'inline-flex h-full items-center rounded-r-full pl-1 pr-2 py-1',
                'text-muted-foreground hover:bg-accent hover:text-foreground transition-colors',
              )}
              aria-label={t('search.clearFilters')}
              title={t('search.clearFilters')}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        {filtersOpen && (
          <div
            // Open the filter menu UPWARD so it doesn't push the
            // result list out of view on tall screens. The popover
            // itself anchors below the input, so the menu rises into
            // the empty space above the filter bar.
            className="absolute left-0 bottom-full z-10 mb-1 min-w-[12rem] rounded-xl border border-border/40 bg-popover py-1 shadow-lg"
            role="menu"
          >
            <div className="px-3 pt-1 pb-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
              Type
            </div>
            {TYPE_KEYS.map((tk) => (
              <button
                key={tk}
                type="button"
                onClick={() => {
                  setType(tk);
                }}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs transition-colors',
                  'hover:bg-accent',
                  type === tk
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                role="menuitem"
              >
                <span>{TYPE_LABELS[tk]}</span>
                {type === tk && <Check className="h-3 w-3 text-primary" />}
              </button>
            ))}
            <div className="my-1 border-t border-border/40" />
            <div className="px-3 pt-1 pb-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
              Scope
            </div>
            {(Object.keys(SCOPE_LABELS) as Scope[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setScope(s);
                }}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs transition-colors',
                  'hover:bg-accent',
                  scope === s
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                role="menuitem"
              >
                <span>{SCOPE_LABELS[s]}</span>
                {scope === s && <Check className="h-3 w-3 text-primary" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/*
        Open discovery — promoted CTA that opens the Zap panel's
        Discover tab in fullscreen. Always visible regardless of type
        filter so users always have a path into the full marketplace.
      */}
      <GradientPillButton icon={Compass} label={t('search.openDiscovery')} onClick={openDiscovery} />
    </div>
  );

  // Create-CTA rows live at the top of the empty-state list. Hidden
  // once the user starts typing so they don't compete with real
  // results. The agent + widget rows for `type=all` share a single
  // grid row so the column stays compact.
  const createRows = (() => {
    if (!showTrending) return null;
    if (type === 'agents') {
      return (
        <ul className="space-y-0.5 pb-1">
          <li>
            <CreateRow
              icon={<Bot className="h-4 w-4 text-primary" />}
              label={tx('search.createAgent', 'Create agent')}
              subtitle={tx('search.createAgentSubtitle', 'Search or create an agent')}
              onClick={() => { void routeToPersonalAgent('agent'); }}
            />
          </li>
        </ul>
      );
    }
    if (type === 'widgets') {
      return (
        <ul className="space-y-0.5 pb-1">
          <li>
            <CreateRow
              icon={<AppWindow className="h-4 w-4 text-primary" />}
              label={tx('search.createWidget', 'Create app')}
              subtitle={tx('search.createWidgetSubtitle', 'Search or create an app')}
              onClick={() => { void routeToPersonalAgent('widget'); }}
            />
          </li>
        </ul>
      );
    }
    if (type === 'all') {
      return (
        <ul className="space-y-0.5 pb-1">
          <li className="grid grid-cols-2 gap-1">
            <CreateRow
              icon={<Bot className="h-4 w-4 text-primary" />}
              label={tx('search.createAgent', 'Create agent')}
              onClick={() => { void routeToPersonalAgent('agent'); }}
              compact
            />
            <CreateRow
              icon={<AppWindow className="h-4 w-4 text-primary" />}
              label={tx('search.createWidget', 'Create app')}
              onClick={() => { void routeToPersonalAgent('widget'); }}
              compact
            />
          </li>
        </ul>
      );
    }
    return null;
  })();

  const renderTrending = () => {
    if (!showTrending) return null;
    if (trendingLoading && !hasTrending) {
      return (
        <div className="px-2 py-2 text-xs text-muted-foreground">
          {tx('discover.loading', 'Loading…')}
        </div>
      );
    }
    if (!hasTrending) {
      // Show a quiet hint if we already have an empty trending set so
      // the popover doesn't feel broken on first open.
      if (type === 'content') {
        return (
          <div className="px-2 py-3 text-xs text-muted-foreground">
            {tx(
              'search.emptyHint',
              `Start typing to search across ${SCOPE_LABELS[scope].toLowerCase()}.`,
              { scope: SCOPE_LABELS[scope].toLowerCase() },
            )}
          </div>
        );
      }
      return null;
    }
    return (
      <>
        {trending.agents.length > 0 && (
          <Section label={tx('search.trendingAgents', 'Trending agents')}>
            {trending.agents.map((a) => (
              <AgentRow key={`ta:${a.id}`} a={a} onClick={() => handleAddAgent(a)} />
            ))}
          </Section>
        )}
        {trending.posts.length > 0 && (
          <Section
            label={
              type === 'widgets'
                ? tx('search.trendingWidgets', 'Trending apps')
                : type === 'posts'
                  ? tx('search.trendingPosts', 'Trending posts')
                  : tx('search.trending', 'Trending')
            }
          >
            {trending.posts.map((p) => (
              <PostRow
                key={`tp:${p.id}`}
                p={p}
                onClick={() => handleAddWidgetPost(p)}
                onTrailingAdd={p.widgetContentId ? () => handleAddWidgetPost(p) : undefined}
              />
            ))}
          </Section>
        )}
      </>
    );
  };

  // Plan v3-05 — URL-detected "Add as widget" row. Sits at the top
  // of the result list so it's the most obvious action when the
  // input is clearly a URL. Inline-only fallback for Tier C — no
  // modal, no delegation CTA.
  const urlImportRow = detectedUrl ? (
    <div className="pb-1 space-y-1">
      <button
        type="button"
        onClick={() => { void handleImportUrl(); }}
        disabled={importingUrl}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors',
          'bg-primary/5 hover:bg-primary/10 text-foreground',
          'disabled:opacity-60',
        )}
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
          <AppWindow className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold">
            {importingUrl
              ? tx('widget.import.progress', 'Importing app…')
              : tx('widget.addFromUrl', 'Add as app')}
          </div>
          <div className="truncate text-[10px] text-muted-foreground">
            {detectedUrl}
          </div>
        </div>
      </button>
      {urlImportNotice && (
        <div
          className={cn(
            'px-2 py-1.5 rounded-md text-[11px] leading-snug',
            urlImportNotice.tone === 'ok' && 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
            urlImportNotice.tone === 'denied' && 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
            urlImportNotice.tone === 'error' && 'bg-rose-500/10 text-rose-700 dark:text-rose-300',
          )}
        >
          {urlImportNotice.text}
        </div>
      )}
    </div>
  ) : null;

  const resultsBody = (
    <>
      {urlImportRow}
      {createRows}
      {createNotice && (
        <div
          role="alert"
          className="mb-1 px-2 py-1.5 rounded-md text-[11px] leading-snug bg-rose-500/10 text-rose-700 dark:text-rose-300"
        >
          {createNotice}
        </div>
      )}
      {loading && (
        <div className="px-2 py-2 text-xs text-muted-foreground">Searching…</div>
      )}
      {!loading && q.trim() && totalCount === 0 && (
        <div className="px-2 py-3 text-xs text-muted-foreground">
          Nothing matches “{q.trim()}”.
        </div>
      )}
      {workspaceMatches.length > 0 && (
        <Section label={t('search.onThisWorkspace')}>
          {workspaceMatches.map((w) => (
            <ResultRow
              key={`w:${w.itemId}`}
              leading={renderWorkspaceLeading(w)}
              title={w.title}
              subtitle={w.subtitle}
              onClick={() => focusWorkspaceItem(w.itemId)}
              trailing={
                w.type === 'agent' || w.contentType === 'widget' ? (
                  <TrailingFocus onClick={() => focusWorkspaceItem(w.itemId)} />
                ) : undefined
              }
            />
          ))}
        </Section>
      )}
      {/*
        Trending always shows on empty query, even when the workspace
        already has items. Previously we suppressed it whenever
        `hasResults` was truthy — but `hasResults` includes
        `workspaceMatches`, which with an empty query simply
        enumerates every item on the canvas. That meant any non-empty
        workspace hid the trending carousel entirely. Trending is
        rendered AFTER `workspaceMatches` so the more contextually
        relevant "what's already here" section reads first; trending
        sits below as a "what could I add?" browser.
      */}
      {!loading && !q.trim() && renderTrending()}
      {results.agents.length > 0 && (
        <Section label={t('workspace.canvas.tabs.agents')}>
          {results.agents.map((a) => (
            <AgentRow key={`a:${a.id}`} a={a} onClick={() => handleAddAgent(a)} />
          ))}
        </Section>
      )}
      {results.posts.length > 0 && (
        <Section label={t('workspace.canvas.tabs.posts')}>
          {results.posts.map((p) => (
            <PostRow
              key={`p:${p.id}`}
              p={p}
              onClick={() =>
                p.widgetContentId
                  ? handleAddWidgetPost(p)
                  : focusOrAdd('post', p.id, {
                      title: p.title,
                      mediaType: p.mediaType,
                      thumbnailUrl: p.thumbnailUrl,
                      mediaUrl: p.mediaUrl,
                    })
              }
              onTrailingAdd={p.widgetContentId ? () => handleAddWidgetPost(p) : undefined}
            />
          ))}
        </Section>
      )}
      {results.contents.length > 0 && (
        <Section label={t('workspace.canvas.tabs.content')}>
          {results.contents.map((c) => (
            <ResultRow
              key={`c:${c.id}`}
              leading={
                c.previewUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={c.previewUrl}
                    alt=""
                    className="h-8 w-8 rounded-md object-cover"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-[10px] font-mono uppercase text-muted-foreground">
                    {(c.contentType ?? 'file').slice(0, 3)}
                  </div>
                )
              }
              title={c.displayName}
              subtitle={c.mimeType}
              onClick={() =>
                focusOrAdd('content', c.id, {
                  displayName: c.displayName,
                  contentType: c.contentType,
                  mimeType: c.mimeType,
                  previewUrl: c.previewUrl,
                })
              }
            />
          ))}
        </Section>
      )}
    </>
  );

  // ---- popover positioning: full-width on mobile, anchored on desktop --
  const popoverStyle: React.CSSProperties | null = useMemo(() => {
    if (headless) {
      // Top-center command-palette overlay (rail variant) — anchored where
      // the legacy top search bar lived, so the rail Search button opens
      // the search in the same place the empty-canvas search sits.
      return {
        position: 'fixed',
        top: 16,
        // Center in the canvas area (right of the rail), not the whole
        // viewport — `--ws-rail-w` is the reserved rail width set by
        // WorkspaceRail. Width subtracts it too, so it never overflows.
        left: 'calc(50% + var(--ws-rail-w, 0px) / 2)',
        transform: 'translateX(-50%)',
        width: 'min(36rem, calc(100vw - var(--ws-rail-w, 0px) - 24px))',
        maxHeight: '70vh',
        zIndex: 200,
      };
    }
    if (!anchorRect) return null;
    if (typeof window === 'undefined') return null;
    const isMobile = window.innerWidth < 640;
    if (isMobile) {
      return {
        position: 'fixed',
        top: Math.round(anchorRect.bottom + 8),
        left: 12,
        right: 12,
        maxHeight: '70vh',
        zIndex: 200,
      };
    }
    return {
      position: 'fixed',
      top: Math.round(anchorRect.bottom + 8),
      left: Math.round(anchorRect.left),
      width: Math.round(anchorRect.width),
      maxHeight: '60vh',
      zIndex: 200,
    };
  }, [anchorRect, headless]);

  const placeholder = type === 'agents'
    ? tx('search.placeholderAgents', 'Search or create an agent…')
    : type === 'widgets'
      ? tx('search.placeholderWidgets', 'Search or create an app…')
      : !user && !isNarrow
        // Anon visitors (desktop) get outward-facing copy — the bar
        // searches the public web of agents/apps. On mobile it would
        // truncate, so keep the short "Search everything…" there.
        ? tx('search.placeholderAnon', 'Search agents and apps across the web…')
        : 'Search everything…';

  // Cmd+K is the global shortcut; render a subtle hint on desktop so
  // users discover it without a tooltip. The hint hides on mobile
  // (no meta key) and once typing starts (the input value would
  // otherwise overlap with the kbd badge). Computed *after mount*
  // (via useEffect) so the SSR pass and the first client pass render
  // the same DOM — touching `navigator.platform` during render
  // produces a hydration mismatch ("Mac" only on the client).
  const [shortcutHint, setShortcutHint] = useState<string | null>(null);
  useEffect(() => {
    const isMac = typeof navigator !== 'undefined'
      && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || '');
    setShortcutHint(isMac ? '⌘K' : 'Ctrl K');
  }, []);

  // Shared search field. A glass pill in the ribbon variant; a plain
  // bordered row docked at the top of the palette in the headless rail
  // variant (which has no in-flow bar of its own).
  const searchBar = (
    <div
      className={cn(
        'flex items-center gap-2.5',
        headless
          ? 'h-12 px-4 border-b border-border/50'
          : 'liquid-glass h-10 rounded-full px-4',
      )}
      data-onboarding-anchor={headless ? undefined : 'search'}
    >
      <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
      <input
        ref={inputRef}
        type="search"
        placeholder={placeholder}
        value={q}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') openDiscovery();
        }}
        className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
      />
      {!q && shortcutHint && (
        <kbd
          aria-hidden
          suppressHydrationWarning
          className="hidden sm:inline-flex shrink-0 items-center rounded-md border border-border/50 bg-muted/40 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground"
        >
          {shortcutHint}
        </kbd>
      )}
    </div>
  );

  // Headless (rail) variant — render only the centered palette + backdrop
  // through portals; no in-flow search bar. The component stays mounted as
  // the palette host and the `workspace:open-search` / Cmd+K listener.
  if (headless) {
    return (
      <>
        {open && typeof document !== 'undefined' &&
          createPortal(
            <div
              className="fixed inset-0 z-[150] bg-background/40 animate-in fade-in duration-150"
              onPointerDown={closePopover}
              aria-hidden
            />,
            document.body,
          )}
        {open && popoverStyle && (
          <PopoverPortal
            ariaLabel="Search everything"
            style={popoverStyle}
            className="glass-panel overflow-hidden rounded-2xl shadow-xl"
          >
            <div ref={portalBodyRef} className="flex max-h-[70vh] flex-col">
              {searchBar}
              {filterBar}
              <div className="min-h-0 flex-1 overflow-y-auto pb-2">
                {resultsBody}
              </div>
            </div>
          </PopoverPortal>
        )}
      </>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        // When the popover is summoned we render a dimmed backdrop
        // (see below) — the container needs to sit above it so the
        // search bar itself stays bright and interactive instead of
        // being shaded along with everything else.
        'pointer-events-auto relative w-full sm:w-[36rem] sm:max-w-full',
        open && summoned && 'z-[160]',
      )}
    >
      <div
        className="liquid-glass flex h-10 items-center gap-2 rounded-full px-4"
        // Onboarding overlay anchor — `search & spawn` annotation.
        // Targets the pill (not the bare <input>) so the rect stays
        // stable when the kbd shortcut hint shrinks/grows.
        data-onboarding-anchor="search"
      >
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          type="search"
          placeholder={placeholder}
          value={q}
          onFocus={() => {
            // Direct focus / click is NOT a summon — keep `summoned`
            // false so the backdrop stays off and the user can keep
            // seeing the canvas behind the popover.
            setOpen(true);
          }}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              openDiscovery();
            }
          }}
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        {!q && shortcutHint && (
          <kbd
            aria-hidden
            suppressHydrationWarning
            className="hidden sm:inline-flex shrink-0 items-center rounded-md border border-border/50 bg-muted/40 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground"
          >
            {shortcutHint}
          </kbd>
        )}
      </div>

      {open && summoned && typeof document !== 'undefined' && createPortal(
        // Dimmed backdrop — only shown when the popover was summoned
        // by FAB / Cmd+K (not on a casual input focus). Portaled to
        // `document.body` so it lives in its own stacking context
        // OUTSIDE the search container — otherwise the container's
        // `z-[160]` stacking context would trap the backdrop and its
        // `backdrop-blur` filter would visually wash over the search
        // bar (which sits inside the same context). With the portal,
        // the backdrop sits crisply between the canvas (below) and
        // the search bar + popover (above). Clicking it closes the
        // popover, matching modal-dialog expectations.
        <div
          className="fixed inset-0 z-[150] bg-background/40 animate-in fade-in duration-150"
          onPointerDown={closePopover}
          aria-hidden
        />,
        document.body,
      )}

      {open && popoverStyle && (
        <PopoverPortal
          ariaLabel="Search results"
          style={popoverStyle}
          className="glass-panel overflow-y-auto rounded-2xl shadow-xl"
        >
          <div ref={portalBodyRef} className="flex flex-col">
            {filterBar}
            <div className="pb-1">{resultsBody}</div>
          </div>
        </PopoverPortal>
      )}
    </div>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="py-1">
      <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

function ResultRow({
  leading,
  title,
  subtitle,
  onClick,
  trailing,
}: {
  leading: React.ReactNode;
  title: string;
  subtitle?: string;
  onClick: () => void;
  /**
   * Optional trailing slot for inline actions (the per-row Grid+ add
   * button on agent / widget post rows). Rendered as a sibling element
   * after the main button so its click doesn't bubble up and double-fire
   * the row click.
   *
   * The outer `<li>` carries a Tailwind `group` class so the trailing
   * action can opt into row-level hover styling via `group-hover:`
   * (see `TrailingAdd`) — hovering the row anywhere lights up the
   * inline action even if the cursor isn't directly over the icon.
   */
  trailing?: React.ReactNode;
}) {
  return (
    <li className="group relative">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors',
          'hover:bg-accent',
          trailing ? 'pr-10' : undefined,
        )}
      >
        <div className="shrink-0">{leading}</div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium">{title}</div>
          {subtitle && (
            <div className="truncate text-[10px] text-muted-foreground">
              {subtitle}
            </div>
          )}
        </div>
      </button>
      {trailing && (
        <div
          className="absolute inset-y-0 right-1.5 flex items-center"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {trailing}
        </div>
      )}
    </li>
  );
}

function AgentRow({
  a,
  onClick,
}: {
  a: AgentResult;
  onClick: () => void;
}) {
  return (
    <ResultRow
      leading={
        <Avatar
          seed={a.username}
          type="agent"
          src={a.avatarUrl}
          className="h-8 w-8"
        />
      }
      title={a.displayName || a.username}
      subtitle={`@${a.username}`}
      onClick={onClick}
      trailing={<TrailingAdd onClick={onClick} />}
    />
  );
}

function PostRow({
  p,
  onClick,
  onTrailingAdd,
}: {
  p: PostResult;
  onClick: () => void;
  onTrailingAdd?: () => void;
}) {
  const preview = postPreviewUrl(p);
  const isWidget = p.widgetContentId != null;
  return (
    <ResultRow
      leading={
        isWidget ? (
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
            <AppWindow className="h-4 w-4 text-primary" />
          </div>
        ) : preview ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={preview}
            alt=""
            className="h-8 w-8 rounded-md object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-[10px] font-mono uppercase text-muted-foreground">
            P
          </div>
        )
      }
      title={p.title || (p.content ?? '').slice(0, 60) || 'Post'}
      subtitle={p.author?.username ? `@${p.author.username}${p.installed ? ' · Installed' : ''}` : (p.installed ? 'Installed' : undefined)}
      onClick={onClick}
      trailing={onTrailingAdd ? <TrailingAdd onClick={onTrailingAdd} installed={p.installed === true} /> : undefined}
    />
  );
}

function TrailingAdd({
  onClick,
  installed,
}: {
  onClick: () => void;
  installed?: boolean;
}) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'p-1.5 rounded-md transition-all duration-150',
        // Resting state — muted icon, no background. The trailing
        // action only "wakes up" once the user is engaging with the
        // row (hovering anywhere on it) or hovering the icon itself,
        // so the search list stays calm at rest.
        installed
          ? 'text-muted-foreground/70 group-hover:text-foreground'
          : 'text-muted-foreground/60 group-hover:text-primary',
        // Group-hover paints a subtle tint so the affordance reads as
        // active even before the cursor reaches the icon. Direct
        // hover on the icon deepens the tint + scales subtly for a
        // crisp "click target" feel.
        installed
          ? 'group-hover:bg-muted/40 hover:bg-muted hover:text-foreground'
          : 'group-hover:bg-primary/5 hover:bg-primary/15 hover:text-primary hover:scale-110',
        'active:scale-95',
      )}
      aria-label={installed ? t('search.focusInstalled') : t('standalone.addToWorkspace')}
    >
      {installed ? <Check className="h-4 w-4" /> : <Grid2x2Plus className="h-4 w-4" />}
    </button>
  );
}

function TrailingFocus({ onClick }: { onClick: () => void }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'p-1.5 rounded-md transition-all duration-150',
        'text-muted-foreground/60 group-hover:text-foreground',
        'group-hover:bg-muted/40 hover:bg-muted hover:scale-110 active:scale-95',
      )}
      aria-label={t('workspace.canvas.actions.focus')}
    >
      <Check className="h-4 w-4" />
    </button>
  );
}

/**
 * `CreateRow` is a plain `<div>` so it can render either as a direct
 * child of `<ul>` (wrapped in `<li>`) or inside a grid-styled `<li>`
 * for the two-up "Create agent / Create widget" pair without
 * producing an invalid `<li>` inside `<li>` tree.
 */
function CreateRow({
  icon,
  label,
  subtitle,
  onClick,
  compact,
}: {
  icon: React.ReactNode;
  label: string;
  subtitle?: string;
  onClick: () => void;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors',
        'bg-primary/5 hover:bg-primary/10 text-foreground',
      )}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-semibold">{label}</div>
        {!compact && subtitle && (
          <div className="truncate text-[10px] text-muted-foreground">
            {subtitle}
          </div>
        )}
      </div>
    </button>
  );
}
